// The sheets of Review with Claude (brief 48): where it does not run, the sign-in it needs; with no
// key, where to add one; with several, which one; then the consent statement and the estimated
// cost before anything is sent, the progress while it runs with a Cancel that stops after the
// window in flight, and what it did.

import type { ProviderKey } from '../../core/keys.ts'
import { consent, costWords, progressWords, SAVE_FIRST, SIGN_IN } from '../core/review.ts'
import type { Estimate, ReviewDone, ReviewProgress } from '../core/types.ts'

export type ReviewState =
  | { stage: 'checking' }
  | { stage: 'elsewhere'; link: string }
  | { stage: 'no-key' }
  | { stage: 'choose'; keys: ProviderKey[] }
  | { stage: 'nothing' }
  | { stage: 'confirm'; key: ProviderKey; estimate: Estimate }
  | { stage: 'running'; key: ProviderKey; progress: ReviewProgress; stopping: boolean }
  | { stage: 'done'; review: ReviewDone; cancelled: boolean }

interface Props {
  state: ReviewState
  onClose: () => void
  onSave: () => void
  onKeys: () => void
  onChoose: (key: ProviderKey) => void
  onReview: () => void
  onStop: () => void
}

const plural = (n: number, one: string, many = `${one}s`) => `${n.toLocaleString('en')} ${n === 1 ? one : many}`

export function ReviewSheet({ state, onClose, onSave, onKeys, onChoose, onReview, onStop }: Props) {
  const box = (label: string, body: React.ReactNode) => (
    <div className="dialog" data-review={state.stage}>
      <div className="dbox review" role="dialog" aria-label={label}>
        <h2>{label}</h2>
        {body}
      </div>
    </div>
  )
  switch (state.stage) {
    case 'checking':
      return box('Review with Claude', <p className="muted">Looking for your keys…</p>)
    case 'elsewhere':
      return box('Review with Claude', <>
        <p data-review-line="sign-in">{SIGN_IN}</p>
        <p data-review-line="save-first">{SAVE_FIRST}</p>
        <div className="dactions">
          <button onClick={onClose}>Close</button>
          <button onClick={onSave} data-review-action="save">Save session</button>
          <a className="button primary" href={state.link} data-review-action="sign-in">Sign in</a>
        </div>
      </>)
    case 'no-key':
      return box('Review with Claude', <>
        <p data-review-line="no-key">No Provider Key for Anthropic is saved on this device. Add one in Settings, under Machine translation’s Provider Keys, then review again.</p>
        <div className="dactions">
          <button onClick={onClose}>Close</button>
          <button className="primary" onClick={onKeys} data-review-action="keys">Open Provider Keys</button>
        </div>
      </>)
    case 'choose':
      return box('Which key?', <>
        <p>Several Provider Keys are for Anthropic. The review uses the one you pick, and remembers it.</p>
        <ul className="choices" data-review-keys>
          {state.keys.map(k => (
            <li key={k.id}><button data-review-key={k.id} onClick={() => onChoose(k)}>{k.name}</button></li>
          ))}
        </ul>
        <div className="dactions"><button onClick={onClose}>Cancel</button></div>
      </>)
    case 'nothing':
      return box('Review with Claude', <>
        <p data-review-line="nothing">Every row is locked, so there is nothing to review.</p>
        <div className="dactions"><button className="primary" autoFocus onClick={onClose}>OK</button></div>
      </>)
    case 'confirm':
      return box('Review with Claude', <>
        <p data-review-line="consent">{consent(state.key, state.estimate)}</p>
        <p data-review-line="cost">{costWords(state.estimate)}</p>
        <p className="muted">Every row Claude changes is flagged, and one Undo puts back the rows as they were.</p>
        <div className="dactions">
          <button onClick={onClose} data-review-action="cancel">Cancel</button>
          <button className="primary" autoFocus onClick={onReview} data-review-action="review">Review</button>
        </div>
      </>)
    case 'running': {
      const share = state.progress.total ? state.progress.done / state.progress.total : 0
      return box('Reviewing with Claude', <>
        <p data-review-line="progress" role="status" aria-live="polite">{progressWords(state.progress)}{state.stopping ? '. Stopping after the window in flight…' : ''}</p>
        <span className="busy-bar"><span style={{ width: `${Math.round(share * 100)}%` }} /></span>
        <div className="dactions"><button onClick={onStop} disabled={state.stopping} data-review-action="stop">Cancel</button></div>
      </>)
    }
    case 'done': {
      const r = state.review
      return box(state.cancelled ? 'Review cancelled' : 'Review done', <>
        <p data-review-line="changed">{r.changed ? `Claude changed ${plural(r.changed, 'row')}, each flagged: Next flagged row walks them.` : 'Claude changed no row.'}</p>
        {r.failed.length > 0 && (
          <div data-review-line="failed">
            <p>{plural(r.failed.length, 'stretch', 'stretches')} kept the aligner’s rows, since no answer for them could be used:</p>
            <ul>{r.failed.map(f => <li key={f.first}>Rows {f.first + 1} to {f.last + 1}: {f.reason}</li>)}</ul>
          </div>
        )}
        {r.stopped && <p data-review-line="stopped" className="alert">The review stopped: {r.stopped}.</p>}
        <p className="muted">{plural(r.input_tokens, 'input token')} and {plural(r.output_tokens, 'output token')} spent.</p>
        <div className="dactions"><button className="primary" autoFocus onClick={onClose}>OK</button></div>
      </>)
    }
  }
}
